import React, { useState } from "react";
import { Link } from "react-router-dom";
import tools from "../../../helpers/tools";
import Page from "../../common/layout/Page";
import Loader from "../../common/Loader";
import Pagination from "../../common/Pagination";
import queue_ft_types from "../../../types/queue_ft.type.json";
import useOrientationJobList from "./useOrientationJobList";
import "./OrientationJobList.scss";

const JobCard = ({
  job,
  retryJob,
  deleteJob,
  retriedJobs,
  retryingJobs,
  amISuperAdmin,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isRetried = retriedJobs.has(job.id);
  const isRetrying = retryingJobs.has(job.id);

  return (
    <div className={`ojl__card ${isRetried ? "ojl__card--retried" : ""}`}>
      <div className="ojl__card-main">
        <div className="ojl__card-body">
          {/* Top row: queue tag + date + redis ID */}
          <div className="ojl__card-top-row">
            <span className="ojl__queue-tag">{job.name}</span>
            <span className="ojl__date">
              <i className="far fa-clock" />
              {tools.formatDate(job.failedAt, "dd/MM/yyyy HH:mm")}
            </span>
            <span className="ojl__redis-id">{job.redisId}</span>
          </div>

          {/* Error message */}
          {job.exception?.message && (
            <div className="ojl__error-message">
              <i className="fas fa-exclamation-circle" />
              <span>{job.exception.message}</span>
            </div>
          )}

          {/* Allocataire link */}
          {job.payload?.allocataireId && (
            <div>
              <Link
                to={tools.getRecipientLinkFromParams(
                  job.payload.allocataireRef || "UserRecipient",
                  job.payload.allocataireId,
                )}
                className="ojl__allocataire-link"
                target="_blank"
              >
                <i className="fas fa-external-link-alt" />
                Voir l'allocataire
              </Link>
            </div>
          )}

          {/* Expand toggle */}
          <button
            className={`ojl__expand-toggle ${expanded ? "ojl__expand-toggle--open" : ""}`}
            onClick={() => setExpanded(!expanded)}
          >
            <i className="fas fa-chevron-right" />
            {expanded ? "Masquer les détails" : "Voir les détails"}
          </button>
        </div>

        {/* Actions */}
        <div className="ojl__card-actions">
          {isRetried ? (
            <button className="ojl__btn ojl__btn--success">
              <i className="fas fa-check-circle" />
              Relancé
            </button>
          ) : isRetrying ? (
            <Loader />
          ) : (
            <>
              <button
                className="ojl__btn ojl__btn--retry"
                onClick={() => retryJob(job.id)}
              >
                <i className="fas fa-redo-alt" />
                Réessayer
              </button>
              {amISuperAdmin && (
                <button
                  className="ojl__btn ojl__btn--danger"
                  onClick={() => deleteJob(job.id)}
                >
                  <i className="fas fa-trash" />
                  Supprimer
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Expanded details: payload + stack trace */}
      {expanded && (
        <div className="ojl__details">
          <div className="ojl__detail-section">
            <h4>Payload</h4>
            <pre className="ojl__code-block">
              {JSON.stringify(job.payload, null, 2)}
            </pre>
          </div>
          <div className="ojl__detail-section">
            <h4>Stack Trace</h4>
            <pre className="ojl__code-block ojl__code-block--error">
              {job.exception?.stack || "Aucune stack trace disponible"}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

const OrientationJobList = () => {
  const {
    jobs,
    isLoading,
    nbPages,
    totalCount,
    search,
    changePage,
    changeNbItemsByPage,
    retryJob,
    retriedJobs,
    retryingJobs,
    fetchJobs,
    handleSearchChange,
    clearFilters,
    deleteJob,
    amISuperAdmin,
  } = useOrientationJobList();

  return (
    <Page
      container="container-fluid px-5"
      title="Liste des jobs échoués"
      bgTransparent
    >
      <div className="ojl">
        {/* Toolbar */}
        <div className="ojl__toolbar">
          <div className="ojl__filters">
            <div className="ojl__filter-group">
              <label htmlFor="ojl-terms">Recherche</label>
              <input
                id="ojl-terms"
                type="text"
                name="terms"
                placeholder="ID, message, payload..."
                value={search.filters.terms}
                onChange={handleSearchChange}
              />
            </div>

            <div className="ojl__filter-group">
              <label htmlFor="ojl-queue">Queue</label>
              <select
                id="ojl-queue"
                name="queue"
                value={search.filters.queue}
                onChange={handleSearchChange}
              >
                <option value="">Toutes les queues</option>
                {Object.entries(
                  queue_ft_types.reduce((acc, q) => {
                    const cat = q.category ?? "Autre";
                    (acc[cat] = acc[cat] || []).push(q);
                    return acc;
                  }, {}),
                ).map(([category, queues]) => (
                  <optgroup key={category} label={category}>
                    {queues.map((q) => (
                      <option key={q.id} value={q.id}>
                        {q.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div className="ojl__filter-group ojl__filter-group--date">
              <label htmlFor="ojl-date-debut">Du</label>
              <input
                id="ojl-date-debut"
                type="date"
                name="dateDebut"
                value={search.filters.dateDebut}
                onChange={handleSearchChange}
                max={search.filters.dateFin || undefined}
              />
            </div>

            <div className="ojl__filter-group ojl__filter-group--date">
              <label htmlFor="ojl-date-fin">Au</label>
              <input
                id="ojl-date-fin"
                type="date"
                name="dateFin"
                value={search.filters.dateFin}
                onChange={handleSearchChange}
                min={search.filters.dateDebut || undefined}
              />
            </div>

            <div className="ojl__filter-actions">
              <button
                className="ojl__btn ojl__btn--primary"
                onClick={fetchJobs}
                disabled={isLoading}
              >
                <i className="fas fa-sync" />
                Rafraichir
              </button>
              <button
                className="ojl__btn ojl__btn--ghost"
                onClick={clearFilters}
                disabled={isLoading}
              >
                Vider les filtres
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="ojl__loading">
            <Loader />
          </div>
        ) : (
          <>
            {/* Count */}
            <div className="ojl__header">
              <span className="ojl__count-badge">
                <i
                  className="fas fa-exclamation-triangle"
                  style={{ color: "#ff991f", fontSize: "0.7rem" }}
                />
                {totalCount} erreur{totalCount > 1 ? "s" : ""}
              </span>
            </div>

            {/* Job cards */}
            {jobs.length > 0 ? (
              <div className="ojl__list">
                {jobs.map((job) => (
                  <JobCard
                    key={job.id}
                    job={job}
                    retryJob={retryJob}
                    deleteJob={deleteJob}
                    retriedJobs={retriedJobs}
                    retryingJobs={retryingJobs}
                    amISuperAdmin={amISuperAdmin}
                  />
                ))}
              </div>
            ) : (
              <div className="ojl__empty">
                <div>
                  <i className="fas fa-check-circle" />
                </div>
                <p>Aucune erreur trouvée</p>
              </div>
            )}

            {/* Pagination */}
            <div className="ojl__pagination">
              <Pagination
                nbPages={nbPages}
                page={search.page}
                changePage={changePage}
                nbItemsByPage={search.nbItemsByPage}
                changeNbItemsByPage={changeNbItemsByPage}
              />
            </div>
          </>
        )}
      </div>
    </Page>
  );
};

export default OrientationJobList;
